/* ------------------------------------------------------------------------------
@name: Dashboard (Merged Overview + Charts)
------------------------------------------------------------------------------ */

import { API_URL } from "variables";
import { HttpRequest, SweetAlert } from "utilities";

const Dashboard = (() => {
  let isLoading = false;
  let isInitialized = false;
  let reloadTimer = null;
  let isInitialLoad = true; // Track if this is the first load

  let donutChart = null;
  let lineChart = null;

  /* -----------------------------
   Date Helpers
  ------------------------------ */
  const toIsoDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${da}`;
  };

  const startOfDay = (iso) => `${iso} 00:00:00`;
  const endOfDay = (iso) => `${iso} 23:59:59`;

  const getLast7DaysParams = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 6); // -6 because today counts as day 1

    return {
      start_date: startOfDay(toIsoDate(sevenDaysAgo)),
      end_date: endOfDay(toIsoDate(today)),
    };
  };

  const buildParamsFromInput = () => {
    const type = $("#type").val();

    const val = $("#dateRange").val();
    if (!val) return null;

    const parts = val.split(" - ");
    if (parts.length !== 2) return null;

    const parseDate = (v) => {
      const [d, m, y] = v.trim().split("/");
      return `${y}-${m}-${d}`;
    };

    return {
      type,
      start_date: startOfDay(parseDate(parts[0])),
      end_date: endOfDay(parseDate(parts[1])),
    };
  };

  const computePreviousPeriod = (start, end) => {
    const s = new Date(start.split(" ")[0]);
    const e = new Date(end.split(" ")[0]);
    const days = Math.round((e - s) / 86400000) + 1;

    const prevEnd = new Date(s);
    prevEnd.setDate(prevEnd.getDate() - 1);

    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - (days - 1));

    return {
      start_date: startOfDay(toIsoDate(prevStart)),
      end_date: endOfDay(toIsoDate(prevEnd)),
      days: days, // Return the number of days for display
    };
  };

  // NEW FUNCTION: Calculate days in selected range
  const calculateDaysInRange = (startDate, endDate) => {
    const s = new Date(startDate.split(" ")[0]);
    const e = new Date(endDate.split(" ")[0]);
    return Math.round((e - s) / 86400000) + 1;
  };

  // NEW FUNCTION: Update period text
  const updatePeriodText = (days) => {
    const periodText = days === 1 ? "vs 1 Day Period" : `vs ${days} Day Period`;
    $(".card-subtext").text(periodText);
  };

  /* -----------------------------
   Format Helpers
  ------------------------------ */
  const formatNumber = (n, d = 0) =>
    Number(n || 0).toLocaleString(undefined, {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    });

  const formatWeight = (v) => `${formatNumber(v, 1)} kg`;
  const formatCbm = (v) => `${formatNumber(v, 3)} m³`;

  /* -----------------------------
   API Helper (sync + async safe)
  ------------------------------ */
  const callAPI = async (url, params) => {
    const fd = new FormData();
    Object.entries(params).forEach(([k, v]) => fd.append(k, v));

    const res = HttpRequest.custom({
      url,
      method: "POST",
      data: fd,
    });

    return typeof res?.then === "function" ? await res : res;
  };

  const callOverview = (p) => callAPI(API_URL.dashboardOverview, p);
  const callStats = (p) => callAPI(API_URL.dashboardStats, p);
  const callCharts = (p) => callAPI(API_URL.dashboardCharts, p);

  /* -----------------------------
   Charts
  ------------------------------ */
  const destroyChart = (chart, elementId) => {
    const $el = $(`#${elementId}`);
    $el.find("svg").remove();
    $el.empty();
    return null;
  };

  const renderDonutChart = (donutData) => {
    donutChart = destroyChart(donutChart, "donut-chart");

    if (!donutData) {
      $("#donut-chart").html(
        '<div class="text-muted text-center" style="padding: 100px 0;">No data</div>'
      );
      return;
    }

    const customer = Number(donutData.customer || 0);
    const reseller = Number(donutData.reseller || 0);

    $("#donut-customer-count").text(formatNumber(customer));
    $("#donut-reseller-count").text(formatNumber(reseller));

    if (customer === 0 && reseller === 0) {
      $("#donut-chart").html(
        '<div class="text-muted text-center" style="padding: 100px 0;">No data available</div>'
      );
      return;
    }

    requestAnimationFrame(() => {
      try {
        donutChart = Morris.Donut({
          element: "donut-chart",
          resize: true,
          data: [
            { label: "Customer", value: customer },
            { label: "Reseller", value: reseller },
          ],
          colors: ["#D22424", "#04805A"],
          formatter: false,
          hideHover: "hide",
        });
        console.log("Donut chart created successfully");
      } catch (error) {
        console.error("Error creating donut chart:", error);
      }
    });
  };

  const renderLineChart = (lineData) => {
    lineChart = destroyChart(lineChart, "line-chart");

    if (!lineData?.dates?.length) {
      $("#line-chart").html(
        '<div class="text-muted text-center" style="padding: 130px 0;">No data</div>'
      );
      return;
    }

    const data = lineData.dates.map((d, i) => ({
      date: d,
      orders: Number(lineData.orders?.[i] || 0),
      weight: Number(lineData.weight?.[i] || 0),
      cbm: Number(lineData.cbm?.[i] || 0),
    }));

    requestAnimationFrame(() => {
      try {
        lineChart = Morris.Line({
          element: "line-chart",
          data,
          xkey: "date",
          ykeys: ["orders", "weight", "cbm"],
          labels: ["Orders", "Weight (kg)", "CBM"],
          behaveLikeLine: true,
          parseTime: false,
          resize: true,
          stacked: true,
          lineWidth: "3px",
          resize: true,
          pointSize: 0,
          hideHover: "auto",
          lineColors: ["#3498db", "#e74c3c", "#2ecc71"],
          preUnits: "",
        });
        console.log("Line chart created successfully");
      } catch (error) {
        console.error("Error creating line chart:", error);
      }
    });
  };

  /* -----------------------------
   Render Helpers
  ------------------------------ */
  const renderStages = (s = {}) => {
    $("#inbound-value").text(formatNumber(s.inbound || 0));
    $("#labels-value").text(formatNumber(s.labels || 0));
    $("#outbound-value").text(formatNumber(s.outbound || 0));
    $("#completed-value").text(formatNumber(s.completed || 0));
    $("#cancelled-value").text(formatNumber(s.cancelled || 0));
  };

  const renderTable = (selector, rows, emptyCols) => {
    const $tbody = $(`${selector} tbody`).empty();
    if (!rows?.length) {
      $tbody.append(
        `<tr><td colspan="${emptyCols}" class="text-center text-muted">No data</td></tr>`
      );
      return;
    }
    rows.forEach((r) => $tbody.append(r));
  };

  const renderTopCustomers = (rows = []) =>
    renderTable(
      "#top-customer-table",
      rows.map(
        (r) => `<tr>
          <td>${r.name || "-"}</td>
          <td class="text-center">${formatNumber(r.orders_count)}</td>
          <td class="text-center">${formatWeight(r.total_weight)}</td>
          <td class="text-center">${formatCbm(r.total_cbm)}</td>
        </tr>`
      ),
      4
    );

  const renderTopResellers = (rows = []) =>
    renderTable(
      "#top-reseller-table",
      rows.map(
        (r) => `<tr>
          <td>${r.reseller || "-"}</td>
          <td class="text-center">${formatNumber(
            r.customers_count || r.customer_count
          )}</td>
          <td class="text-center">${formatNumber(r.orders_count)}</td>
          <td class="text-center">${formatWeight(r.total_weight)}</td>
          <td class="text-center">${formatCbm(r.total_cbm)}</td>
        </tr>`
      ),
      5
    );

  /* -----------------------------
   KPI
  ------------------------------ */
  const renderKpi = (valEl, deltaEl, cur, prev) => {
    const diff = cur - prev;
    const pct =
      prev === 0
        ? cur === 0
          ? 0
          : 100
        : Math.round((diff / Math.abs(prev)) * 100);

    // Determine the type and format accordingly
    const isWeight = valEl.includes("weight");
    const isCbm = valEl.includes("cbm");

    // Format the main value
    $(valEl).text(
      isWeight ? formatWeight(cur) : isCbm ? formatCbm(cur) : formatNumber(cur)
    );

    // Format the delta value with the same formatting as the main value
    const formattedDiff = isWeight
      ? formatWeight(Math.abs(diff))
      : isCbm
      ? formatCbm(Math.abs(diff))
      : formatNumber(Math.abs(diff));

    $(deltaEl)
      .css("color", pct >= 0 ? "#28a745" : "#e74c3c")
      .html(
        `<i class="ph ${pct >= 0 ? "ph-trend-up" : "ph-trend-down"}"></i> ${
          pct >= 0 ? "+" : "-"
        }${formattedDiff} (${pct >= 0 ? "+" : ""}${pct}%)`
      );
  };

  /* -----------------------------
   Loader
  ------------------------------ */
  const loadDashboard = async () => {
    if (isLoading) {
      return;
    }

    isLoading = true;

    try {
      const params = buildParamsFromInput();

      if (!params) {
        isLoading = false;
        return;
      }

      // UPDATED: Calculate and update period text
      const daysInRange = calculateDaysInRange(
        params.start_date,
        params.end_date
      );
      updatePeriodText(daysInRange);

      $(".stat-value").text("...");
      $("#donut-chart").html(
        '<div class="text-center" style="padding: 100px 0;">Loading...</div>'
      );
      $("#line-chart").html(
        '<div class="text-center" style="padding: 130px 0;">Loading...</div>'
      );

      const prev = computePreviousPeriod(params.start_date, params.end_date);

      // On initial load, use 7 days for charts, otherwise use date range
      const chartParams = isInitialLoad ? getLast7DaysParams() : params;

      const [overview, stats, prevStats, charts] = await Promise.all([
        callOverview(params), // KPIs, stages, tables use date range
        callStats(params), // KPIs use date range
        callStats(prev), // Previous period for comparison
        callCharts(chartParams), // Charts use 7 days on initial load
      ]);

      if (overview?.code === 200) {
        renderStages(overview.data.stage_counts);
        renderTopCustomers(overview.data.top_customers);
        renderTopResellers(overview.data.top_resellers);
      }

      if (stats?.code === 200) {
        renderKpi(
          "#orders-value",
          "#orders-delta",
          stats.data.orders_count,
          prevStats?.data?.orders_count || 0
        );
        renderKpi(
          "#weight-value",
          "#weight-delta",
          stats.data.total_weight,
          prevStats?.data?.total_weight || 0
        );
        renderKpi(
          "#cbm-value",
          "#cbm-delta",
          stats.data.total_cbm,
          prevStats?.data?.total_cbm || 0
        );
      }

      if (charts?.code === 200 && charts.data) {
        if (charts.data.donut) {
          renderDonutChart(charts.data.donut);
        }

        if (charts.data.line) {
          renderLineChart(charts.data.line);
        }
      } else {
        $("#donut-chart").html(
          '<div class="text-center text-danger" style="padding: 100px 0;">Failed to load chart</div>'
        );
        $("#line-chart").html(
          '<div class="text-center text-danger" style="padding: 130px 0;">Failed to load chart</div>'
        );
      }

      // After first load, mark as no longer initial
      if (isInitialLoad) {
        isInitialLoad = false;
      }
    } catch (e) {
      SweetAlert?.config("Failed to load dashboard", "error");
      console.error("Dashboard error:", e);
    } finally {
      isLoading = false;
    }
  };

  /* -----------------------------
   UI
  ------------------------------ */
  const bindUI = () => {
    const $dateRange = $("#dateRange");

    $dateRange.on("apply.daterangepicker", function (ev, picker) {
      clearTimeout(reloadTimer);
      reloadTimer = setTimeout(() => {
        loadDashboard();
      }, 100);
    });
  };

  const init = () => {
    if (!$(".js-dashboard").length) {
      return;
    }

    isInitialized = true;
    // Set default date range input to today
    if (!$("#dateRange").val()) {
      const d = new Date();
      const today = `${String(d.getDate()).padStart(2, "0")}/${String(
        d.getMonth() + 1
      ).padStart(2, "0")}/${d.getFullYear()}`;
      $("#dateRange").val(`${today} - ${today}`);
    }

    bindUI();

    setTimeout(() => {
      loadDashboard(); // isInitialLoad=true, so charts will use 7 days
    }, 500);
  };

  return { init };
})();

export default Dashboard;
